import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { FilterOption, SelectFilterField } from '@/components/common/AdvancedFilter';
import { useDebounce } from '@/hooks/useDebounce';
import { OPTION_DETAIL_SEPARATOR } from '@/utils/filters';
import { memberInstitutesQueryOptions } from '../api/question.api';
import type { MemberInstitute } from '../types/question.types';

function toInstituteOptions(institutes: MemberInstitute[] | undefined): FilterOption[] {
  return (institutes ?? []).map((institute) => ({
    value: String(institute.instituteId),
    label: institute.instituteName ?? `#${institute.instituteId}`,
    description: [
      institute.instituteCode === null ? null : String(institute.instituteCode),
      institute.instituteEiin,
      institute.divisionName,
      institute.districtName,
      institute.thanaName,
    ]
      .filter(Boolean)
      .join(OPTION_DETAIL_SEPARATOR),
  }));
}

export function useInstituteFilterField(
  selectedId: string,
  memberId: number | undefined,
): SelectFilterField {
  const [query, setQuery] = useState('');
  const searchText = useDebounce(query.trim());

  const { data, isFetching } = useQuery(memberInstitutesQueryOptions(memberId, searchText));

  const results = toInstituteOptions(data);
  const isSelectedInResults =
    selectedId === '' || results.some((option) => option.value === selectedId);
  const options = isSelectedInResults
    ? results
    : [{ value: selectedId, label: `Institute #${selectedId}` }, ...results];

  return {
    key: 'instituteId',
    label: 'Select Institution',
    type: 'select',
    placeholder: memberId === undefined ? 'Pick a ZM first' : 'All institutions',
    options,
    onSearchChange: setQuery,
    isSearching: isFetching,
    disabled: memberId === undefined,
    disabledHint: 'Pick a ZM Name first',
  };
}
